import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Link2, Sparkles, ArrowRight } from "lucide-react";
import { API, api, PUBLIC_ORIGIN } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Footer } from "@/components/Footer";
import { RankBadge } from "@/components/landing/ClipVisuals";
import { Reactions } from "@/components/Reactions";
import { PublicSkeleton } from "@/components/Skeletons";

const RATIO = { "9:16": "aspect-[9/16] max-w-[380px]", "16:9": "aspect-video max-w-[900px]", "1:1": "aspect-square max-w-[600px]" };

const Missing = ({ t }) => (
  <div className="min-h-[70vh] grid place-items-center px-5 text-center" data-testid="shared-clip-missing">
    <div>
      <h1 className="font-display text-3xl font-bold text-white">{t("sh.missing")}</h1>
      <Link to="/" className="inline-flex mt-6 cx-btn rounded-full px-6 py-3 text-sm font-semibold">{t("sh.discover")}</Link>
    </div>
  </div>
);

export default function SharedClip() {
  const { id } = useParams();
  const { t } = useI18n();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/s/${id}/data`).then((r) => {
      setD(r.data);
      localStorage.setItem("clipx_ref", JSON.stringify({ sid: id, at: Date.now() }));
    }).catch(() => setD(false));
  }, [id]);
  if (d === null) return <PublicSkeleton testid="shared-clip-loading" />;
  const copy = async () => { await navigator.clipboard.writeText(`${PUBLIC_ORIGIN}/api/s/${id}`).catch(() => {}); toast.success(t("sh.copied")); };
  return (
    <div className="min-h-screen bg-[#04050a] text-slate-200" data-testid="shared-clip-page">
      <header className="h-16 px-5 sm:px-8 flex items-center justify-between border-b border-white/[0.06] bg-[#0b0e1a]/90 backdrop-blur-xl">
        <Link to="/" data-testid="shared-logo" className="ml-24 sm:ml-28"><img src="/logo-wide.png" alt="CLIP-EX" className="h-8" /></Link>
        <Link to="/login?mode=register" data-testid="shared-header-cta" className="cx-btn rounded-full px-5 py-2 text-sm font-semibold">{t("ld.cta")}</Link>
      </header>
      {!d ? <Missing t={t} /> : (
        <main className="relative max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14 grid lg:grid-cols-[auto_1fr] gap-10 items-start">
          <div className="absolute -top-20 left-0 w-[420px] h-[420px] rounded-full bg-[#2f7bff]/15 blur-[120px] pointer-events-none" />
          <div className={`relative w-full mx-auto ${RATIO[d.aspect] || RATIO["9:16"]} rounded-3xl p-[5px] bg-gradient-to-b from-[#2f7bff]/60 via-white/10 to-[#a746ff]/60 lg:w-[380px] ${d.aspect === "16:9" ? "lg:w-[640px]" : ""}`}>
            <video src={`${API}/s/${id}/video.mp4`} poster={`${API}/s/${id}/thumb.jpg`} controls playsInline autoPlay muted loop
              className="w-full h-full rounded-[1.3rem] bg-black object-contain" data-testid="shared-clip-video" />
          </div>
          <div className="relative cx-rise">
            <div className="flex flex-wrap items-center gap-2">
              {d.creator && (d.handle ? <Link to={`/u/${d.handle}`} className="cx-label hover:text-white" data-testid="shared-clip-creator">{d.creator} · <span className="normal-case">@{d.handle}</span></Link>
                : <div className="cx-label" data-testid="shared-clip-creator">{d.creator}</div>)}
              {d.badge && <RankBadge badge={d.badge} testid="shared-clip-badge" />}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3 leading-tight" data-testid="shared-clip-title">{d.title}</h1>
            {d.hook && <p className="text-lg text-[#c79bff] mt-3">{d.hook}</p>}
            {d.description && <p className="text-slate-400 mt-4 leading-relaxed">{d.description}</p>}
            {d.hashtags?.length > 0 && <p className="font-mono text-sm text-[#3ad7ff] mt-4">{d.hashtags.join(" ")}</p>}
            <div className="mt-6"><Reactions sid={id} initial={d.reactions} size="lg" testid="shared-reactions" /></div>
            <div className="flex items-center gap-4 mt-6 text-xs text-slate-500 font-mono">
              <span className="flex items-center gap-1.5" data-testid="shared-clip-views"><Eye className="w-3.5 h-3.5" />{d.views} {t("sh.views")}</span>
              <span>{d.duration}s · {d.aspect}</span>
              <button onClick={copy} data-testid="shared-clip-copy-btn" className="flex items-center gap-1.5 text-[#3ad7ff] hover:text-white"><Link2 className="w-3.5 h-3.5" />{t("sh.copy")}</button>
            </div>
            <div className="mt-10 cx-card cx-brackets rounded-2xl p-6 sm:p-7" data-testid="shared-made-with">
              <div className="flex items-center gap-2 text-xs text-[#3ad7ff] font-mono"><Sparkles className="w-3.5 h-3.5" />{t("sh.made_with")}</div>
              <p className="font-display text-xl font-semibold text-white mt-3">{t("sh.pitch")}</p>
              <p className="text-sm text-slate-400 mt-2">{t("ld.cta_hint")}</p>
              <Link to="/login?mode=register" data-testid="shared-cta-register" className="group inline-flex mt-5 cx-btn rounded-full px-6 py-3 text-sm font-semibold items-center gap-2">
                {t("sh.cta")}<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </main>
      )}
      <Footer />
    </div>
  );
}
